import { useState } from 'react';
import { Trash2, Undo2 } from 'lucide-react';
import { Button } from '@/shared/components';
import type { ArrivalAnnotation, ArrivalAnnotationPaletteResponse } from '@/shared/api';
import { ANNOTATION_KIND_LABELS, type AnnotationTool } from '@/shared/annotations';
import { cn } from '@/shared/lib/cn';
import { ArrivalImageCanvas } from './ArrivalImageCanvas';

interface Props {
  stepId: string;
  imageId: string;
  initial: ArrivalAnnotation[];
  palette: ArrivalAnnotationPaletteResponse;
  saving: boolean;
  onSave: (annotations: ArrivalAnnotation[]) => void;
  onClose: () => void;
}

/**
 * Zeichnen auf dem Bild: Werkzeug und Farbe wählen, dann auf dem Bild ziehen. Gespeichert
 * werden Daten, nicht Pixel — das Original bleibt unangetastet.
 */
export function ArrivalAnnotationEditor(props: Props) {
  const { stepId, imageId, initial, palette, saving, onSave, onClose } = props;
  const [items, setItems] = useState<ArrivalAnnotation[]>(initial);
  const [tool, setTool] = useState<AnnotationTool>((palette.kinds[0] as AnnotationTool) ?? 'arrow');
  const [color, setColor] = useState(palette.colors[0] ?? '#b53220');
  const full = items.length >= palette.maxItems;

  return (
    <div className="border-line bg-card space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1">
          {palette.kinds.map((kind) => (
            <Button
              key={kind}
              size="sm"
              variant={tool === kind ? 'brand' : 'ghost'}
              onClick={() => setTool(kind as AnnotationTool)}
            >
              {ANNOTATION_KIND_LABELS[kind] ?? kind}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {palette.colors.map((option) => (
            <button
              key={option}
              type="button"
              aria-label={`Farbe ${option}`}
              onClick={() => setColor(option)}
              style={{ backgroundColor: option }}
              className={cn(
                'size-6 rounded-full border transition-transform',
                color === option ? 'border-ink scale-110' : 'border-line',
              )}
            />
          ))}
        </div>
      </div>

      <p className="text-ink-soft text-xs">
        {full
          ? `Höchstens ${palette.maxItems} Markierungen je Bild.`
          : 'Auf dem Bild ziehen — für einen Punkt genügt ein Klick.'}
      </p>

      <ArrivalImageCanvas
        stepId={stepId}
        imageId={imageId}
        variant="full"
        annotations={items}
        className="aspect-video w-full max-w-2xl cursor-crosshair"
        draw={
          full
            ? undefined
            : { tool, color, onAdd: (annotation) => setItems((all) => [...all, annotation]) }
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Undo2 size={13} strokeWidth={2} />}
            disabled={items.length === 0}
            onClick={() => setItems((all) => all.slice(0, -1))}
          >
            Letzte zurück
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Trash2 size={13} strokeWidth={2} />}
            disabled={items.length === 0}
            onClick={() => setItems([])}
          >
            Alle entfernen
          </Button>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={onClose}>
            Abbrechen
          </Button>
          <Button size="sm" variant="brand" loading={saving} onClick={() => onSave(items)}>
            Markierungen speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
